import { activityIdentityForJob, isActivityExportJobType } from './identity';
import { hasConflictingActivityBuildingIds } from './job';
import { parseActivityMessage } from './message';
import type {
    ActivityItem,
    ActivityJob,
    ActivityObservedIntake,
} from './models';
import { parseStoredActivityJobWithIntegrity } from './storage-job';
import { MAX_STORED_JOBS_PER_ITEM } from './storage-limits';
import {
    hasDuplicates,
    isRecord,
    keyMatchesActivityItem,
    parseActivityKey,
    parseArray,
    parseAvailability,
    parseNullableNonEmptyString,
    parseObservedJobIds,
    parseTimestamp,
} from './storage-validation';

export function parseStoredActivityItems(
    value: unknown
): readonly ActivityItem[] | null {
    if (!isRecord(value)) return null;

    const key = parseActivityKey(value['key']);
    const correlationId = parseNullableNonEmptyString(value['correlationId']);
    const jobsValue = value['jobs'];
    const createdAtUtc = parseTimestamp(value['createdAtUtc']);
    const updatedAtUtc = parseTimestamp(value['updatedAtUtc']);
    const availability = parseAvailability(value['availability']);
    const identityQuarantined = value['identityQuarantined'];
    const observedIntake = parseObservedIntake(value['observedIntake']);

    if (
        !key ||
        correlationId === undefined ||
        !Array.isArray(jobsValue) ||
        jobsValue.length === 0 ||
        jobsValue.length > MAX_STORED_JOBS_PER_ITEM ||
        !createdAtUtc ||
        !updatedAtUtc ||
        typeof value['seen'] !== 'boolean' ||
        typeof value['actedOn'] !== 'boolean' ||
        (identityQuarantined !== undefined &&
            typeof identityQuarantined !== 'boolean') ||
        observedIntake === null ||
        !availability
    ) {
        return null;
    }

    const parsedJobs = parseArray(
        jobsValue,
        parseStoredActivityJobWithIntegrity
    );
    const observedJobIds = parseObservedJobIds(value['observedJobIds']);
    if (
        !parsedJobs ||
        !observedJobIds ||
        hasDuplicates(parsedJobs.map(({ job }) => job.jobId)) ||
        parsedJobs.some(({ job }) => !observedJobIds.includes(job.jobId))
    ) {
        return null;
    }
    const jobs = parsedJobs.map(({ job }) => job);
    const hasExportIdentityConflict = parsedJobs.some(
        ({ exportIdentityConflicted }) => exportIdentityConflicted
    );

    const common = {
        correlationId,
        createdAtUtc,
        updatedAtUtc,
        seen: value['seen'],
        actedOn: value['actedOn'],
        availability,
        identityQuarantined:
            identityQuarantined === true || hasExportIdentityConflict,
        observedIntake,
    } as const;
    if (keyMatchesActivityItem(key, correlationId, jobs)) {
        return [createStoredItem({ ...common, key, jobs, observedJobIds })];
    }

    return migrateLegacyCorrelatedExport(key, jobs, common);
}

function migrateLegacyCorrelatedExport(
    key: ActivityItem['key'],
    jobs: readonly ActivityJob[],
    common: {
        readonly correlationId: string | null;
        readonly createdAtUtc: string;
        readonly updatedAtUtc: string;
        readonly seen: boolean;
        readonly actedOn: boolean;
        readonly availability: ActivityItem['availability'];
        readonly identityQuarantined: boolean;
        readonly observedIntake: ActivityObservedIntake | undefined;
    }
): readonly ActivityItem[] | null {
    const legacyKey = common.correlationId
        ? `batch:${common.correlationId.toLowerCase()}`
        : null;
    if (
        key !== legacyKey ||
        !jobs.some((job) => isActivityExportJobType(job.jobType))
    ) {
        return null;
    }

    const grouped = new Map<ActivityItem['key'], ActivityJob[]>();
    for (const job of jobs) {
        const identity = activityIdentityForJob(
            job.jobType,
            job.jobId,
            common.correlationId
        );
        if (!identity.supported) return null;
        grouped.set(identity.key, [...(grouped.get(identity.key) ?? []), job]);
    }

    return [...grouped.entries()].map(([migratedKey, migratedJobs]) =>
        createStoredItem({
            ...common,
            key: migratedKey,
            jobs: migratedJobs,
            observedJobIds: migratedJobs.map((job) => job.jobId),
            createdAtUtc: activityJobTimestamp(migratedJobs, 'oldest'),
            updatedAtUtc: activityJobTimestamp(migratedJobs, 'newest'),
            observedIntake: migratedJobs.some((job) => job.stage !== 'export')
                ? common.observedIntake
                : undefined,
        })
    );
}

function createStoredItem(input: {
    readonly key: ActivityItem['key'];
    readonly correlationId: string | null;
    readonly jobs: readonly ActivityJob[];
    readonly observedJobIds: readonly string[];
    readonly createdAtUtc: string;
    readonly updatedAtUtc: string;
    readonly seen: boolean;
    readonly actedOn: boolean;
    readonly availability: ActivityItem['availability'];
    readonly identityQuarantined: boolean;
    readonly observedIntake?: ActivityObservedIntake;
}): ActivityItem {
    const hasConflictingBuildings = hasConflictingActivityBuildingIds(
        input.jobs
    );
    return {
        key: input.key,
        correlationId: input.correlationId,
        jobs: input.jobs,
        observedJobIds: input.observedJobIds,
        createdAtUtc: input.createdAtUtc,
        updatedAtUtc: input.updatedAtUtc,
        seen: input.seen,
        actedOn: input.actedOn,
        availability:
            input.identityQuarantined || hasConflictingBuildings
                ? 'unavailable'
                : input.availability,
        identityQuarantined:
            input.identityQuarantined || hasConflictingBuildings,
        ...(input.observedIntake
            ? { observedIntake: input.observedIntake }
            : {}),
    };
}

function activityJobTimestamp(
    jobs: readonly ActivityJob[],
    order: 'oldest' | 'newest'
): string {
    return jobs.reduce((selected, candidate) => {
        const selectedTime = Date.parse(selected.occurredAtUtc);
        const candidateTime = Date.parse(candidate.occurredAtUtc);
        return order === 'oldest'
            ? candidateTime < selectedTime
                ? candidate
                : selected
            : candidateTime > selectedTime
              ? candidate
              : selected;
    }).occurredAtUtc;
}

function parseObservedIntake(
    value: unknown
): ActivityObservedIntake | null | undefined {
    if (value === undefined) return undefined;
    if (!isRecord(value)) return null;

    const jobType = value['jobType'];
    const message = parseActivityMessage(value['message']);
    const occurredAtUtc = parseTimestamp(value['occurredAtUtc']);
    if (
        (jobType !== 'DocumentImport' && jobType !== 'SharePointImport') ||
        message === undefined ||
        !occurredAtUtc
    ) {
        return null;
    }

    return { jobType, message, occurredAtUtc };
}
